/**
 * FG ↔ Dispatch Reconciliation · V28-005 · READ-ONLY
 *
 * Reconciles Finished Goods stock against Delivered Domestic DC + Export EDC +
 * Stock Engine V2 (FG OUT) + FG receipts + valid reversals/returns. Strictly
 * read-only — no heal / adjust / post / reverse controls. Backed by
 * `GET /api/fg-reconciliation` (single canonical service that reuses the
 * verified V28-005 logic). KG and METRES are shown separately and never summed.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";

const fmt = (v) => Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 });

const STATUS_STYLE = {
  "MATCHED": "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
  "REVERSED / REDISPATCHED": "border-sky-500/40 bg-sky-500/10 text-sky-300",
  "REVERSED": "border-sky-500/40 bg-sky-500/10 text-sky-300",
  "MISSING FG OUT": "border-red-500/40 bg-red-500/10 text-red-300",
  "PARTIAL FG OUT": "border-amber-500/40 bg-amber-500/10 text-amber-300",
  "EXCESS FG OUT": "border-orange-500/40 bg-orange-500/10 text-orange-300",
  "DUPLICATE FG OUT": "border-orange-500/40 bg-orange-500/10 text-orange-300",
  "BATCH MISMATCH": "border-fuchsia-500/40 bg-fuchsia-500/10 text-fuchsia-300",
  "PRODUCT MISMATCH": "border-fuchsia-500/40 bg-fuchsia-500/10 text-fuchsia-300",
  "NO FG RECEIPT": "border-red-500/40 bg-red-500/10 text-red-300",
  "BALANCE VARIANCE": "border-red-500/40 bg-red-500/10 text-red-300",
  "REVIEW REQUIRED": "border-amber-500/40 bg-amber-500/10 text-amber-300",
};

function StatusBadge({ status }) {
  const cls = STATUS_STYLE[status] || "border-zinc-500/40 bg-zinc-500/10 text-zinc-300";
  return (
    <span className={`inline-block whitespace-nowrap text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm border ${cls}`} data-testid={`status-badge-${status}`}>
      {status}
    </span>
  );
}

const EMPTY_FILTERS = {
  date_from: "", date_to: "", doc_type: "all", doc_no: "", so_eso: "",
  customer: "", product_code: "", batch: "", status: "", exceptions_only: false,
};

const inputCls = "h-9 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm font-mono text-xs text-white placeholder-zinc-600 focus:outline-none focus:border-[var(--accent)]";

export default function FGDispatchReconciliation() {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("batch"); // 'batch' | 'document'
  const [drill, setDrill] = useState(null); // drilldown payload
  const [drillLoading, setDrillLoading] = useState(false);

  const load = useCallback(async (f) => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(f).forEach(([k, v]) => { if (v !== "" && v !== false) params[k] = v; });
      const { data } = await api.get("/fg-reconciliation", { params });
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load reconciliation");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(EMPTY_FILTERS); }, [load]);

  const set = (k, v) => setFilters((p) => ({ ...p, [k]: v }));
  const onSearch = () => load(filters);
  const onReset = () => { setFilters(EMPTY_FILTERS); load(EMPTY_FILTERS); };
  const onRefresh = () => load(filters);

  const summary = data?.summary || {};
  const documents = data?.documents || [];
  const batches = data?.batches || [];
  const statuses = data?.statuses || [];

  const openDrill = async (dc_id) => {
    setDrillLoading(true); setDrill({ loading: true });
    try {
      const { data } = await api.get("/fg-reconciliation/drilldown", { params: { dc_id } });
      setDrill(data);
    } catch (e) {
      toast.error("Failed to load drill-down"); setDrill(null);
    } finally { setDrillLoading(false); }
  };

  const exportCsv = () => {
    const rows = tab === "batch"
      ? [["Product Code", "Batch", "Warehouse", "FG Received (m)", "Domestic DC OUT (m)", "Export EDC OUT (m)", "Other FG OUT (m)", "Reversals/Returns (m)", "Expected Balance (m)", "Current Balance (m)", "Variance (m)", "Status"],
         ...batches.map(b => [b.product_code, b.batch, b.warehouse_code, b.fg_received_m, b.domestic_dc_out_m, b.export_edc_out_m, b.other_fg_out_m, b.reversals_returns_m, b.expected_balance_m, b.current_balance_m, b.variance_m, b.status])]
      : [["Type", "DC/EDC No", "Delivery Date", "SO/ESO", "Customer", "Delivered Qty (m)", "Delivered Weight (kg)", "FG OUT Posted (m)", "Gross OUT", "Reversed", "Effective OUT", "Status"],
         ...documents.map(d => [d.type, d.dc_no, d.delivery_date, d.so_eso, d.customer, d.delivered_qty_m, d.delivered_weight_kg, d.fg_out_posted_m, d.gross_fg_out_legs, d.reversed_fg_out_legs, d.effective_fg_out_legs, d.status])];
    const csv = rows.map(r => r.map(c => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `fg-dispatch-reconciliation-${tab}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const varColor = (v) => Math.abs(Number(v || 0)) > 1e-4 ? "text-red-300 font-semibold" : "text-emerald-300";
  const eq = data?.stock_flow_equation;

  // [label, value, colorClass, subLine, tooltip, testid]
  const primaryCards = [
    ["FG Received", `${fmt(summary.fg_received_m)} m`, "text-sky-300", null, "Physical FG received into stock (FG-Receipt IN), counted once per batch.", "card-fg-received"],
    ["Net Physical FG OUT", `${fmt(summary.net_physical_fg_out_m)} m`, "text-white", null, "Actual net FG stock dispatched after valid reversals (Gross FG OUT − Reversed FG OUT).", "card-net-physical-out"],
    ["Current FG Balance", `${fmt(summary.current_fg_balance_m)} m`, "text-amber-300", null, "Current on-hand FG per Stock Engine V2 balance.", "card-current-balance"],
    ["Expected FG Balance", `${fmt(summary.expected_fg_balance_m)} m`, "text-amber-300", null, "FG Received − Net Physical FG OUT (+ valid returns).", "card-expected-balance"],
    ["Variance", `${fmt(summary.variance_m)} m`, varColor(summary.variance_m), null, "Current − Expected. Target 0.", "card-variance"],
  ];
  const activityCards = [
    ["Delivered Dispatch (Gross)", `${fmt(summary.delivered_dispatch_gross_m)} m`, "text-zinc-200", `${fmt(summary.delivered_dispatch_weight_kg)} kg`, "Total quantity across Delivered DC/EDC documents, including documents later reversed or superseded.", "card-delivered-gross"],
    ["FG OUT Posted (Gross)", `${fmt(summary.fg_out_posted_gross_m)} m`, "text-zinc-200", null, "Total FG dispatch OUT postings before reversal effects.", "card-fg-out-gross"],
    ["Reversals / Returns", `+${fmt(summary.reversals_returns_m)} m`, "text-sky-300", null, "FG returned to stock through valid reversal transactions.", "card-reversals"],
  ];
  const statusCards = [
    ["Matched", summary.matched_documents ?? 0, "text-emerald-300", null, "Documents reconciling cleanly (incl. reversed / redispatched).", "card-matched"],
    ["Exceptions", summary.exception_documents ?? 0, (summary.exception_documents > 0 ? "text-red-300" : "text-zinc-300"), null, "Documents needing attention. Target 0 for daily stock control.", "card-exceptions"],
  ];
  const Card = ([l, v, c, extra, tip, tid]) => (
    <div key={l} title={tip || undefined} data-testid={tid} className="border border-[var(--border)] bg-[var(--bg-1)] rounded-sm p-4">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{l}</div>
      <div className={`text-2xl font-mono tabular-nums mt-1 ${c}`}>{v}</div>
      {extra && <div className="text-[11px] font-mono tabular-nums text-zinc-400 mt-0.5">{extra}</div>}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="fg-dispatch-reconciliation">
      <PageHeader
        overline="Inventory · Live FG Stock Control · READ-ONLY"
        title="FG ↔ Dispatch Reconciliation"
        subtitle="Reconciles Finished Goods against Delivered Domestic DC + Export EDC, Stock Engine V2 (FG OUT), FG receipts and valid reversals. Detection only — this report never posts, adjusts or heals stock. KG and METRES are reported separately."
      />

      {/* summary cards */}
      <div className="space-y-4" data-testid="recon-summary-cards">
        {/* primary physical / net stock-flow */}
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[var(--accent)] mb-2">Physical / Net Stock Flow</div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">{primaryCards.map(Card)}</div>
          {eq && (
            <div className="mt-2 text-[11px] font-mono text-zinc-400" data-testid="stock-flow-equation">
              <span className="text-zinc-500">{eq.text}</span>
              <span className="mx-2 text-zinc-700">·</span>
              <span className="text-zinc-600">{eq.gross_breakdown}</span>
            </div>
          )}
        </div>
        {/* gross document / audit activity */}
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Gross Document / Audit Activity</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">{activityCards.map(Card)}</div>
        </div>
        {/* operational status */}
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Operational Status</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">{statusCards.map(Card)}</div>
        </div>
      </div>

      {/* filters */}
      <div className="border border-[var(--border)] bg-[var(--bg-1)] rounded-sm p-4 space-y-3" data-testid="recon-filters">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
          <label className="flex flex-col gap-1"><span className="text-[9px] uppercase tracking-widest text-zinc-500">Date From</span>
            <input type="date" data-testid="filter-date-from" className={inputCls} value={filters.date_from} onChange={e => set("date_from", e.target.value)} /></label>
          <label className="flex flex-col gap-1"><span className="text-[9px] uppercase tracking-widest text-zinc-500">Date To</span>
            <input type="date" data-testid="filter-date-to" className={inputCls} value={filters.date_to} onChange={e => set("date_to", e.target.value)} /></label>
          <label className="flex flex-col gap-1"><span className="text-[9px] uppercase tracking-widest text-zinc-500">Document Type</span>
            <select data-testid="filter-doc-type" className={inputCls} value={filters.doc_type} onChange={e => set("doc_type", e.target.value)}>
              <option value="all">All</option><option value="domestic">Domestic DC</option><option value="export">Export EDC</option>
            </select></label>
          <label className="flex flex-col gap-1"><span className="text-[9px] uppercase tracking-widest text-zinc-500">Status</span>
            <select data-testid="filter-status" className={inputCls} value={filters.status} onChange={e => set("status", e.target.value)}>
              <option value="">All</option>{statuses.map(s => <option key={s} value={s}>{s}</option>)}
            </select></label>
          <label className="flex flex-col gap-1"><span className="text-[9px] uppercase tracking-widest text-zinc-500">DC / EDC No.</span>
            <input data-testid="filter-doc-no" className={inputCls} placeholder="DC / EDC…" value={filters.doc_no} onChange={e => set("doc_no", e.target.value)} /></label>
          <label className="flex flex-col gap-1"><span className="text-[9px] uppercase tracking-widest text-zinc-500">SO / ESO</span>
            <input data-testid="filter-so-eso" className={inputCls} placeholder="SO / ESO…" value={filters.so_eso} onChange={e => set("so_eso", e.target.value)} /></label>
          <label className="flex flex-col gap-1"><span className="text-[9px] uppercase tracking-widest text-zinc-500">Customer / Buyer</span>
            <input data-testid="filter-customer" className={inputCls} placeholder="Customer…" value={filters.customer} onChange={e => set("customer", e.target.value)} /></label>
          <label className="flex flex-col gap-1"><span className="text-[9px] uppercase tracking-widest text-zinc-500">Product Code</span>
            <input data-testid="filter-product" className={inputCls} placeholder="Product…" value={filters.product_code} onChange={e => set("product_code", e.target.value)} /></label>
          <label className="flex flex-col gap-1"><span className="text-[9px] uppercase tracking-widest text-zinc-500">Batch / Lot</span>
            <input data-testid="filter-batch" className={inputCls} placeholder="Batch…" value={filters.batch} onChange={e => set("batch", e.target.value)} /></label>
          <label className="flex items-end gap-2 pb-1">
            <input type="checkbox" data-testid="filter-exceptions-only" className="accent-[var(--accent)] w-4 h-4" checked={filters.exceptions_only} onChange={e => set("exceptions_only", e.target.checked)} />
            <span className="text-[10px] uppercase tracking-widest text-zinc-400">Exceptions Only</span>
          </label>
        </div>
        <div className="flex flex-wrap gap-2">
          <button data-testid="btn-search" onClick={onSearch} className="h-9 px-4 bg-[var(--accent)] text-black rounded-sm text-xs font-mono uppercase tracking-widest hover:opacity-90 transition-opacity">Search</button>
          <button data-testid="btn-reset" onClick={onReset} className="h-9 px-4 border border-[var(--border)] text-zinc-300 rounded-sm text-xs font-mono uppercase tracking-widest hover:bg-[var(--bg-2)]/40">Reset</button>
          <button data-testid="btn-refresh" onClick={onRefresh} className="h-9 px-4 border border-[var(--border)] text-zinc-300 rounded-sm text-xs font-mono uppercase tracking-widest hover:bg-[var(--bg-2)]/40">Refresh</button>
          <button data-testid="btn-export" onClick={exportCsv} className="h-9 px-4 border border-[var(--border)] text-zinc-300 rounded-sm text-xs font-mono uppercase tracking-widest hover:bg-[var(--bg-2)]/40">Export CSV</button>
          <button data-testid="btn-print" onClick={() => window.print()} className="h-9 px-4 border border-[var(--border)] text-zinc-300 rounded-sm text-xs font-mono uppercase tracking-widest hover:bg-[var(--bg-2)]/40">Print</button>
        </div>
      </div>

      {/* tabs */}
      <div className="flex gap-2" data-testid="recon-tabs">
        <button data-testid="tab-batch" onClick={() => setTab("batch")} className={`h-9 px-4 rounded-sm text-xs font-mono uppercase tracking-widest border ${tab === "batch" ? "bg-[var(--bg-2)]/50 border-[var(--accent)] text-white" : "border-[var(--border)] text-zinc-400"}`}>By Batch (Stock Tally)</button>
        <button data-testid="tab-document" onClick={() => setTab("document")} className={`h-9 px-4 rounded-sm text-xs font-mono uppercase tracking-widest border ${tab === "document" ? "bg-[var(--bg-2)]/50 border-[var(--accent)] text-white" : "border-[var(--border)] text-zinc-400"}`}>By Dispatch Document</button>
      </div>

      {/* BY BATCH */}
      {tab === "batch" && (
        <div className="border border-[var(--border)] rounded-sm overflow-auto" data-testid="batch-table">
          <table className="w-full text-sm">
            <thead className="bg-[var(--bg-2)]/30 text-[10px] uppercase tracking-widest text-zinc-500">
              <tr>
                <th className="text-left px-3 py-2">Product Code</th>
                <th className="text-left px-3 py-2">Batch</th>
                <th className="text-left px-3 py-2 w-24">Warehouse</th>
                <th className="text-right px-3 py-2">FG Recd (m)</th>
                <th className="text-right px-3 py-2">Dom DC OUT (m)</th>
                <th className="text-right px-3 py-2">Exp EDC OUT (m)</th>
                <th className="text-right px-3 py-2">Other OUT (m)</th>
                <th className="text-right px-3 py-2">Reversals (m)</th>
                <th className="text-right px-3 py-2">Expected (m)</th>
                <th className="text-right px-3 py-2">Current (m)</th>
                <th className="text-right px-3 py-2">Variance (m)</th>
                <th className="text-left px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={12} className="px-3 py-6 text-center text-zinc-500 text-xs font-mono uppercase tracking-widest">Loading…</td></tr>}
              {!loading && batches.length === 0 && <tr><td colSpan={12} className="px-3 py-8 text-center text-zinc-500 text-xs">No batches match the filters</td></tr>}
              {!loading && batches.map((b) => (
                <tr key={`${b.product_code}::${b.batch}`} data-testid={`batch-row-${b.batch}`} className="border-t border-[var(--border)]/60 hover:bg-[var(--bg-2)]/20">
                  <td className="px-3 py-2 font-mono text-[var(--accent)] break-words max-w-[260px] whitespace-normal">{b.product_code}</td>
                  <td className="px-3 py-2 font-mono text-zinc-200">{b.batch}</td>
                  <td className="px-3 py-2 font-mono text-[10px] text-zinc-500">{b.warehouse_code}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-sky-300">{fmt(b.fg_received_m)}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-zinc-300">{fmt(b.domestic_dc_out_m)}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-zinc-300">{fmt(b.export_edc_out_m)}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-zinc-400">{fmt(b.other_fg_out_m)}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-sky-300">{fmt(b.reversals_returns_m)}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-amber-300">{fmt(b.expected_balance_m)}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-white">{fmt(b.current_balance_m)}</td>
                  <td className={`px-3 py-2 text-right font-mono tabular-nums ${varColor(b.variance_m)}`} data-testid={`batch-variance-${b.batch}`}>{fmt(b.variance_m)}</td>
                  <td className="px-3 py-2"><StatusBadge status={b.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* BY DOCUMENT */}
      {tab === "document" && (
        <div className="border border-[var(--border)] rounded-sm overflow-auto" data-testid="document-table">
          <table className="w-full text-sm">
            <thead className="bg-[var(--bg-2)]/30 text-[10px] uppercase tracking-widest text-zinc-500">
              <tr>
                <th className="text-left px-3 py-2 w-24">Type</th>
                <th className="text-left px-3 py-2">DC / EDC No.</th>
                <th className="text-left px-3 py-2 w-28">Delivery Date</th>
                <th className="text-left px-3 py-2">SO / ESO</th>
                <th className="text-left px-3 py-2">Customer / Buyer</th>
                <th className="text-right px-3 py-2">Delivered (m)</th>
                <th className="text-right px-3 py-2">Weight (kg)</th>
                <th className="text-right px-3 py-2">FG OUT (m)</th>
                <th className="text-right px-3 py-2 w-20">Rev / Net</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2 w-20">Detail</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={11} className="px-3 py-6 text-center text-zinc-500 text-xs font-mono uppercase tracking-widest">Loading…</td></tr>}
              {!loading && documents.length === 0 && <tr><td colSpan={11} className="px-3 py-8 text-center text-zinc-500 text-xs">No documents match the filters</td></tr>}
              {!loading && documents.map((d) => (
                <tr key={d.dc_no} data-testid={`document-row-${d.dc_no}`} className="border-t border-[var(--border)]/60 hover:bg-[var(--bg-2)]/20">
                  <td className="px-3 py-2"><span className={`text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm border ${d.is_export ? "border-purple-500/40 bg-purple-500/10 text-purple-300" : "border-teal-500/40 bg-teal-500/10 text-teal-300"}`}>{d.is_export ? "EDC" : "DC"}</span></td>
                  <td className="px-3 py-2 font-mono text-[var(--accent)]">{d.dc_no}</td>
                  <td className="px-3 py-2 font-mono text-[11px] text-zinc-400">{d.delivery_date || "—"}</td>
                  <td className="px-3 py-2 font-mono text-[11px] text-zinc-300">{d.so_eso || "—"}</td>
                  <td className="px-3 py-2 text-zinc-300 break-words max-w-[220px] whitespace-normal">{d.customer || "—"}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-white">{fmt(d.delivered_qty_m)}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-zinc-400">{fmt(d.delivered_weight_kg)}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-zinc-200">{fmt(d.fg_out_posted_m)}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-[11px] text-zinc-400">{d.reversed_fg_out_legs}/{d.effective_fg_out_legs}</td>
                  <td className="px-3 py-2"><StatusBadge status={d.status} /></td>
                  <td className="px-3 py-2">
                    <button data-testid={`drill-${d.dc_no}`} onClick={() => openDrill(d.dc_id)} className="text-[10px] font-mono uppercase tracking-widest text-[var(--accent)] hover:underline">View →</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data?.read_only && (
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-600" data-testid="read-only-banner">
          Read-only report · no stock mutation · Stock Engine V2 is the source of truth
        </div>
      )}

      {/* drill-down modal */}
      {drill && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="drill-modal" onClick={() => setDrill(null)}>
          <div className="bg-[var(--bg-1)] border border-[var(--border)] rounded-sm max-w-4xl w-full max-h-[85vh] overflow-auto p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div className="text-sm font-mono uppercase tracking-widest text-white">Genealogy · {drill?.dc?.dc_no || "…"}</div>
              <button data-testid="drill-close" onClick={() => setDrill(null)} className="text-zinc-400 hover:text-white text-lg leading-none">×</button>
            </div>
            {(drillLoading || drill.loading) && <div className="text-xs text-zinc-500 font-mono uppercase tracking-widest py-6 text-center">Loading…</div>}
            {!drillLoading && !drill.loading && (
              <>
                {drill.genealogy_error && <div className="text-xs text-red-300 font-mono border border-red-500/40 bg-red-500/10 rounded-sm px-3 py-2">{drill.genealogy_error}</div>}
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Delivered Legs (DC → PL → PS → box → FG batch)</div>
                  <table className="w-full text-xs">
                    <thead className="text-[9px] uppercase tracking-widest text-zinc-600"><tr><th className="text-left py-1">PS No.</th><th className="text-left py-1">Box</th><th className="text-left py-1">Barcode (Batch)</th><th className="text-left py-1">Product</th><th className="text-right py-1">Qty (m)</th></tr></thead>
                    <tbody>{(drill.legs || []).map((l, i) => (<tr key={i} className="border-t border-[var(--border)]/50"><td className="py-1 font-mono text-zinc-300">{l.ps_no}</td><td className="py-1 font-mono text-zinc-400">{l.box_no}</td><td className="py-1 font-mono text-[var(--accent)]">{l.barcode}</td><td className="py-1 font-mono text-zinc-400 break-words max-w-[200px] whitespace-normal">{l.product_code}</td><td className="py-1 text-right font-mono tabular-nums text-white">{fmt(l.qty)}</td></tr>))}</tbody>
                  </table>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Stock Engine V2 Movements (IN / OUT / Reversal)</div>
                  <table className="w-full text-xs" data-testid="drill-v2-movements">
                    <thead className="text-[9px] uppercase tracking-widest text-zinc-600"><tr><th className="text-left py-1">Date</th><th className="text-left py-1">Type</th><th className="text-left py-1">Dir</th><th className="text-left py-1">Batch</th><th className="text-right py-1">Qty</th><th className="text-left py-1">Source</th><th className="text-left py-1">Posting Key</th></tr></thead>
                    <tbody>{(drill.v2_movements || []).map((m, i) => (<tr key={i} className="border-t border-[var(--border)]/50"><td className="py-1 font-mono text-[10px] text-zinc-400">{(m.transaction_datetime || "").slice(0, 19).replace("T", " ")}</td><td className="py-1 font-mono text-zinc-300">{m.transaction_type}</td><td className={`py-1 font-mono ${m.direction === "OUT" ? "text-red-300" : "text-emerald-300"}`}>{m.direction}</td><td className="py-1 font-mono text-zinc-400">{m.batch_lot}</td><td className="py-1 text-right font-mono tabular-nums text-white">{fmt(m.quantity)}</td><td className="py-1 font-mono text-[10px] text-zinc-500">{m.source_document_type}/{m.source_document_no}</td><td className="py-1 font-mono text-[9px] text-zinc-600 break-all max-w-[160px] whitespace-normal">{m.idempotency_key}</td></tr>))}</tbody>
                  </table>
                </div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-600">Read-only genealogy · no edit / delete / post controls</div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
